import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  shorthands,
  Button,
  Card,
  Spinner,
  MessageBar,
  MessageBarBody,
  Text,
  tokens,
  Field,
  Label,
} from '@fluentui/react-components';
import { PersonRegular } from '@fluentui/react-icons';
import axios from 'axios';
import { apiConfig } from '../authConfig';
import { useAuth } from '../hooks/useAuth';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('20px'),
  },
  card: {
    ...shorthands.padding('20px'),
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '200px 1fr',
    ...shorthands.gap('15px'),
    alignItems: 'center',
  },
  label: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  value: {
    color: tokens.colorNeutralForeground1,
  },
  responseBox: {
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.padding('15px'),
    ...shorthands.borderRadius('4px'),
    fontFamily: 'monospace',
    fontSize: '12px',
    overflowX: 'auto',
    maxHeight: '400px',
    overflowY: 'auto',
  },
});

function UserInfoTab() {
  const classes = useStyles();
  const { getAccessToken, accounts } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);

  const fetchUserInfo = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessToken();
      const response = await axios.get(`${apiConfig.apiBaseUrl}/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserInfo(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserInfo();
  }, []);

  const localAccount = accounts[0];

  return (
    <div className={classes.container}>
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <Card className={classes.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <PersonRegular fontSize={24} />
          <Text size={500} weight="semibold">Local User Info (MSAL)</Text>
        </div>
        <div className={classes.infoGrid}>
          <Text className={classes.label}>Username:</Text>
          <Text className={classes.value}>{localAccount?.name || 'N/A'}</Text>
          
          <Text className={classes.label}>Account ID:</Text>
          <Text className={classes.value}>{localAccount?.username || 'N/A'}</Text>
          
          <Text className={classes.label}>Environment:</Text>
          <Text className={classes.value}>{localAccount?.environment || 'N/A'}</Text>
          
          <Text className={classes.label}>Tenant ID:</Text>
          <Text className={classes.value} style={{ wordBreak: 'break-all' }}>
            {localAccount?.tenantId || 'N/A'}
          </Text>
        </div>
      </Card>

      <Card className={classes.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <Text size={500} weight="semibold">API Server User Info</Text>
          <Button appearance="subtle" onClick={fetchUserInfo} disabled={loading}>
            Refresh
          </Button>
        </div>

        {loading && <Spinner label="Loading..." />}

        {!loading && userInfo && (
          <div className={classes.infoGrid}>
            <Text className={classes.label}>OID:</Text>
            <Text className={classes.value} style={{ wordBreak: 'break-all' }}>
              {userInfo.user?.oid || 'N/A'}
            </Text>
            
            <Text className={classes.label}>UPN:</Text>
            <Text className={classes.value}>{userInfo.user?.upn || 'N/A'}</Text>
            
            <Text className={classes.label}>Name:</Text>
            <Text className={classes.value}>{userInfo.user?.name || 'N/A'}</Text>
            
            <Text className={classes.label}>App ID:</Text>
            <Text className={classes.value} style={{ wordBreak: 'break-all' }}>
              {userInfo.user?.appid || 'N/A'}
            </Text>
            
            <Text className={classes.label}>Roles:</Text>
            <Text className={classes.value}>
              {userInfo.user?.roles?.length > 0 ? userInfo.user.roles.join(', ') : 'None'}
            </Text>
          </div>
        )}
      </Card>

      {userInfo && (
        <Card className={classes.card}>
          <Text size={500} weight="semibold" block style={{ marginBottom: '15px' }}>
            Full Response (JSON)
          </Text>
          <div className={classes.responseBox}>
            <pre>{JSON.stringify(userInfo, null, 2)}</pre>
          </div>
        </Card>
      )}
    </div>
  );
}

export default UserInfoTab;